import { useState, useEffect, useCallback } from 'react';

export interface QueuedResponse {
  id: string;
  conversationId: string;
  scheduledFor: string;
  scheduledDelayMinutes: number;
  generatedResponse: string;
  finalResponse: string;
  executionStatus: 'pending' | 'processing' | 'paused';
  automationRule: {
    id: string;
    instructions: string;
    goal: string;
  };
  createdAt: string;
  metadata?: {
    triggerMessage?: string;
    confidence?: number;
    modelUsed?: string;
  };
}

interface UseQueuedResponsesResult {
  queuedResponses: QueuedResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useQueuedResponses(conversationId: string | null): UseQueuedResponsesResult {
  const [queuedResponses, setQueuedResponses] = useState<QueuedResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQueuedResponses = useCallback(async () => {
    if (!conversationId) {
      setQueuedResponses([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/automation/${conversationId}/logs?status=pending,processing,paused&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch queued responses: ${response.statusText}`);
      }

      const data = await response.json();
      
      // The backend returns data in data.data.logs format
      const logs = data.data?.logs || data.logs || [];
      
      // Filter for pending/processing executions that have scheduled times
      const queued = logs.filter((log: any) => {
        const isQueuedStatus = log.executionStatus === 'pending' || log.executionStatus === 'processing' || log.executionStatus === 'paused';
        
        // Calculate scheduledFor if not provided (backward compatibility)
        let scheduledFor = log.scheduledFor;
        if (!scheduledFor && log.scheduledDelayMinutes && log.triggeredAt) {
          const triggeredTime = new Date(log.triggeredAt).getTime();
          const delayMs = log.scheduledDelayMinutes * 60 * 1000;
          scheduledFor = new Date(triggeredTime + delayMs).toISOString();
        }
        
        const hasScheduledTime = scheduledFor;
        const isInFuture = hasScheduledTime && new Date(scheduledFor) > new Date();
        
        return isQueuedStatus && hasScheduledTime && isInFuture;
      }).map((log: any) => {
        // Calculate scheduledFor if not provided (backward compatibility)
        let scheduledFor = log.scheduledFor;
        if (!scheduledFor && log.scheduledDelayMinutes && log.triggeredAt) {
          const triggeredTime = new Date(log.triggeredAt).getTime();
          const delayMs = log.scheduledDelayMinutes * 60 * 1000;
          scheduledFor = new Date(triggeredTime + delayMs).toISOString();
        }
        
        return {
          id: log.id,
          conversationId: log.conversationId,
          scheduledFor: scheduledFor,
          scheduledDelayMinutes: log.scheduledDelayMinutes || 0,
          generatedResponse: log.generatedResponse || '',
          finalResponse: log.finalResponse || log.generatedResponse || '',
          executionStatus: log.executionStatus,
          automationRule: log.automationRule || { id: '', instructions: '', goal: '' },
          createdAt: log.createdAt,
          metadata: log.metadata
        };
      });

      setQueuedResponses(queued);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Failed to fetch queued responses:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchQueuedResponses();

    // Poll for updates every 10 seconds
    const interval = setInterval(fetchQueuedResponses, 10000);

    return () => clearInterval(interval);
  }, [fetchQueuedResponses]);

  return {
    queuedResponses,
    loading,
    error,
    refetch: fetchQueuedResponses
  };
} 